import React, { Suspense } from 'react';
import { apm } from '@elastic/apm-rum';
import { useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router';
import { useEffectOnce } from 'react-use';

import { JOB_TYPE } from '../../common/constants';
import { getJobDraftPath } from '../../common/employerJobPaths';
import { useJobSuspense } from '../../common/hooks/requests/useJob';
import { useGetSessionCompany } from '../../common/hooks/useGetSessionCompany';
import useIsKeywordMatchingEnabled from '../../common/hooks/useIsKeywordMatchingEnabled';
import { JobResponse } from '../../common/interfaces/responses';
import ContainerV2 from '../../components/ContainerV2';
import ErrorBoundaryWithCrashReporting from '../../components/Error/ErrorBoundaryWithCrashReporting';
import { getSessionCompanyCountryCode } from '../../selectors/session';
import {
  makeRequiredFormDefaultValues,
  SelectedQuestionsFormState,
} from './common/constants';
import useGetJobRoleWithQuestions from './components/JobRequiredForm/JobRequirements/MatchCriteria/useGetJobRoleQuestions';
import useConvertJobData from './hooks/useConvertJobData';
import useDraft from './hooks/useDraft';

const JobCopyRedirectionComponent = () => {
  const { jobId } = useParams<{
    jobId: string;
  }>();
  const { sessionCompany } = useGetSessionCompany();
  const { job } = useJobSuspense({ jobId });
  const isJobFromCurrentCompany = job?.company?.id === sessionCompany?.id;

  const { createDraft } = useDraft();
  const countryCode = useSelector(getSessionCompanyCountryCode);
  const { convertJobFormStateToPayload, convertResponseToFormState } =
    useConvertJobData();

  const history = useHistory();
  const isKeywordMatchingEnabled = useIsKeywordMatchingEnabled();
  const jobRoleWithQuestions = useGetJobRoleWithQuestions(
    job?.HierarchicalJobCategoryId ?? '',
    isKeywordMatchingEnabled
  );

  const keywordMatchingExcludedQuestions: SelectedQuestionsFormState[] = [
    SelectedQuestionsFormState.SKILL_PROFICIENCY,
    SelectedQuestionsFormState.WORK_EXPERIENCE_DURATION,
    SelectedQuestionsFormState.INDUSTRY_EXPERIENCE,
    SelectedQuestionsFormState.DOCUMENT_CERTIFICATE_AVAILABILITY,
  ];

  useEffectOnce(() => {
    const createDraftAndRedirect = async () => {
      if (!isJobFromCurrentCompany) {
        history.replace('/403');
        return;
      }

      const formState = convertResponseToFormState(
        {
          data: {
            ...job,
            type: job?.type === JOB_TYPE.DAILY ? JOB_TYPE.PART_TIME : job?.type,
          },
        } as JobResponse,
        { isCopy: true }
      );

      const shouldUseMatchCriteria =
        isKeywordMatchingEnabled &&
        Boolean(jobRoleWithQuestions?.questions?.length);

      if (formState.screeningQuestions?.selectedQuestions) {
        formState.screeningQuestions.selectedQuestions =
          formState.screeningQuestions.selectedQuestions.filter(
            sq =>
              !(
                shouldUseMatchCriteria &&
                keywordMatchingExcludedQuestions.includes(sq)
              )
          );
      }

      try {
        const response = await createDraft(
          convertJobFormStateToPayload(
            makeRequiredFormDefaultValues(countryCode, formState)
          )
        );
        sessionStorage.setItem(`jobDraftIsCopy:${response.data.id}`, 'true');
        history.replace(getJobDraftPath(response.data.id));
      } catch (error) {
        apm.captureError(error as Error);
        history.replace('/500');
      }
    };
    createDraftAndRedirect();
  });

  return <ContainerV2 isLoading={true} />;
};

export default function JobCopyRedirection() {
  return (
    <ErrorBoundaryWithCrashReporting fallback={<Redirect to="/500" />}>
      <Suspense fallback={<ContainerV2 isLoading={true} />}>
        <JobCopyRedirectionComponent />
      </Suspense>
    </ErrorBoundaryWithCrashReporting>
  );
}
